import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLocation } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

export function useScrollAnimations() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Wait a tick for the DOM to render after a route change
    const timer = setTimeout(() => {
      const ctx = gsap.context(() => {
        // Refresh ScrollTrigger to ensure correct measurements
        ScrollTrigger.refresh();

        const fadeUpElements = gsap.utils.toArray('.gsap-fade-up') as HTMLElement[];

        fadeUpElements.forEach((el) => {
          gsap.fromTo(
            el,
            { opacity: 0, y: 40 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: el,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        });

        const staggerElements = gsap.utils.toArray('.gsap-stagger-container') as HTMLElement[];

        staggerElements.forEach((container) => {
          const items = container.querySelectorAll('.gsap-stagger-item');
          if (items.length > 0) {
            gsap.fromTo(
              items,
              { opacity: 0, y: 30 },
              {
                opacity: 1,
                y: 0,
                duration: 0.6,
                stagger: 0.1,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: container,
                  start: 'top 80%',
                  toggleActions: 'play none none reverse',
                },
              }
            );
          }
        });

        // Pillars Section Interactive Logic
        const pillarContents = gsap.utils.toArray('.pillar-content') as HTMLElement[];

        if (document.querySelector('#pillars-section')) {
          // Set initial states
          gsap.set('.pillar-content', { opacity: 0, y: 50, pointerEvents: 'none' });
          gsap.set('.pillar-content-0', { opacity: 1, y: 0, pointerEvents: 'auto' });
          gsap.set('.pillar-label', { opacity: 0.3, x: 0 });
          gsap.set('.pillar-label-0', { opacity: 1, x: 10 });
          gsap.set('.pillar-dot', { scale: 1, backgroundColor: '#050505', borderColor: '#14b8a6' });
          gsap.set('.pillar-dot-0', { scale: 1.8, backgroundColor: '#14b8a6', borderColor: '#fff' });

          const pillarTimeline = gsap.timeline({
            scrollTrigger: {
              trigger: '#pillars-section',
              start: 'top top',
              end: '+=140%', // Scroll for 3x the viewport height
              scrub: 1,
              pin: true, // Pin the section itself
              anticipatePin: 1,
            }
          });

          // Orbital rotation synced to scroll
          pillarTimeline.to('.pillar-dot', {
            rotation: 360,
            ease: 'none',
            duration: pillarContents.length
          }, 0);

          pillarContents.forEach((content, i) => {
            const position = i * 1; // 1 unit per pillar

            // Fade in (except first one which is already in)
            if (i > 0) {
              pillarTimeline.to(content, {
                opacity: 1,
                y: 0,
                pointerEvents: 'auto',
                duration: 1
              }, position);

              pillarTimeline.to(`.pillar-label-${i}`, { opacity: 1, x: 10, duration: 0.5 }, position);
              pillarTimeline.to(`.pillar-dot-${i}`, {
                scale: 1.8,
                backgroundColor: '#14b8a6',
                borderColor: '#fff',
                duration: 0.5
              }, position);
            }

            // Fade out (if not last)
            if (i < pillarContents.length - 1) {
              pillarTimeline.to(content, {
                opacity: 0,
                y: -50,
                pointerEvents: 'none',
                duration: 1
              }, position + 1);

              pillarTimeline.to(`.pillar-label-${i}`, { opacity: 0.3, x: 0, duration: 0.5 }, position + 1);
              pillarTimeline.to(`.pillar-dot-${i}`, {
                scale: 1,
                backgroundColor: '#050505',
                borderColor: '#14b8a6',
                duration: 0.5
              }, position + 1);
            }
          });
        }
      });

      return () => {
        ctx.revert();
      };
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname]);
}
